import { PureComponent, createRef } from 'react';
import classNames from 'classnames/bind';
import autoBind from 'react-autobind';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import SentimentsSvc from '../../../../services/dbServices/SentimentsSvc';
import TopicsSvc from '../../../../services/TopicsSvc';
import SectorsSvc from '../../../../services/SectorsSvc';
import {
  getPeriodType,
  generateTopicChartData,
  getAverageSentimentText,
} from '../../../../helpers/sentimentHelpers';
import { getTrendingBarAssetQuery } from '../../../../helpers/watchlistHelperFunctions';
import {
  defaultChartGraphData, defaultChartFillGraphData,
} from '../../../../data/sentimentAnalysis';
import {
  defaultSentimentPeriod, defaultMiniChartLayout,
} from '../../../../data/directory/topics/topicsSentimentAnalysis';
import { SECTORS_PATH_ITEM } from '../../../../data/directory/constants';

import PeriodFilters from '../PeriodFilters';
import Preloader from '../../../Preloader';
import Plot from '../../../Plot';
import SentimentModal from '../SentimentTags/SentimentModal';
import TooltipOutside from '../TooltipOutside';
import Tooltip from '../../../Tooltip';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

class SentimentMiniChart extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isChartLoading: true,
      isNoData: false,
      isChartHidden: false,
      activePeriod: defaultSentimentPeriod,
      modalActivePeriod: defaultSentimentPeriod,
      chartGraph: {
        ...defaultChartGraphData,
        connectgaps: true,
        x: [],
        y: [],
        line: {
          ...defaultChartGraphData.line,
          width: 2,
        },
      },
      miniChartTooltip: false,
      chartLayout: {
        ...defaultMiniChartLayout,
        width: 174,
        height: 75,
      },
      selectedTopic: null,
      boxChartTooltip: false,
      modalTabActive: '',
    };

    autoBind(this);
    this.sentimentChartBoxRef = createRef();
    this.showTooltipTime = null;
    this.showStoryModal = null;

    this.tooltipRef = createRef();
    this.periodFiltersRef = createRef();
  }

  componentDidMount() {
    const {
      isTopic, isFullLocations, inView,
    } = this.props;
    if ((isTopic || isFullLocations) && inView) {
      this.generateTopicData();
    }
    this.setChartSize();
  }

  componentDidUpdate(prevProps) {
    const {
      isTopic, isFullLocations, topic, inView, activePeriod, width,
    } = this.props;

    if (((isTopic || isFullLocations) && inView && inView !== prevProps.inView)
      || (activePeriod !== prevProps.activePeriod && inView)
      || (isTopic && topic?.id !== prevProps.topic?.id)
    ) {
      this.generateTopicData();
    }

    if (width !== prevProps.width) {
      this.setChartSize();
    }
  }

  setChartSize() {
    const { width: screenWidth, isSingleLine, isTopic } = this.props;
    let width = 174;
    let height = 75;
    if (screenWidth >= 2800) {
      if (isSingleLine) {
        width = 250;
        height = 57;
      } else if (isTopic) {
        width = 250;
        height = 60;
      } else {
        width = 383;
        height = 164;
      }
    } else if (screenWidth > 767) {
      if (isSingleLine) {
        width = 125;
        height = 29;
      } else if (isTopic) {
        width = 125;
        height = 30;
      } else {
        width = 174;
        height = 75;
      }
    } else if (isSingleLine) {
      width = 125;
      height = 27;
    } else if (isTopic) {
      width = 91;
      height = 27;
    } else {
      width = 140;
      height = 52;
    }

    const zerolinewidth = screenWidth >= 2800 ? 2 : 1;
    this.setState({
      chartLayout: {
        ...defaultMiniChartLayout,
        width,
        height,
        yaxis: {
          ...defaultMiniChartLayout.yaxis,
          zerolinewidth,
        },
      },
    });
  }

  async callTopicAPI(params) {
    try {
      const { data } = await SentimentsSvc.getTopicSentiments(params);
      return data;
    } catch (e) {
      console.log(e);
    }
    return { data: [] };
  }

  generateChartData(newData) {
    const { width } = this.props;
    const chartGraph = {
      ...defaultChartGraphData,
      x: newData.x,
      y: newData.y,
      connectgaps: true,
      line: {
        ...defaultChartGraphData.line,
        width: width >= 2800 ? 4 : 2,
        color: newData.chartColor,
      },
    };
    return {
      chartColor: newData.chartColor,
      chartGraph,
      chartGraphArr: [chartGraph, {
        ...defaultChartFillGraphData,
        x: newData.x,
        y: Array(newData.x.length).fill(-100),
        fillcolor: newData.chartColor === '#777777' ? '#F9F9F9' : `${newData.chartColor}19`,
      }],
    };
  }

  async generateTopicData(modalPeriodCall = false) {
    this.setState({
      isChartLoading: true,
    });

    const { activePeriod, modalActivePeriod } = this.state;
    const {
      isTopic, slugType, topic, isFullLocations, isInsideModal,
      activePeriod: propsActivePeriod,
    } = this.props;

    let currentPeriod = isFullLocations && !isInsideModal ? activePeriod : propsActivePeriod;
    if (modalPeriodCall) {
      currentPeriod = modalActivePeriod;
    }

    const periodTypeObj = getPeriodType(currentPeriod);
    const params = {
      period: periodTypeObj.period,
      duration: 'daily',
    };

    if (isTopic) {
      params.entity_type = slugType;
      params.entity_id = topic.id;
    } else if (isFullLocations) {
      params.entity_type = 'Continent';
    }

    const data = await this.callTopicAPI(params);
    const newData = generateTopicChartData(data, periodTypeObj, true);
    const { chartGraph } = this.generateChartData(newData);

    this.setState({
      ...(!modalPeriodCall && {
        chartGraph,
        avgPoint: newData.avgPoint,
      }),
      isNoData: !newData.x.length,
      isChartHidden: newData.x.length <= 5,
      isChartLoading: false,
    });
  }

  handlePeriodChange(value) {
    this.setState({
      activePeriod: value,
    }, () => {
      this.generateTopicData();
    });
  }

  handleModalPeriodChange(value) {
    const { isFullLocations } = this.props;
    if (isFullLocations) {
      this.handlePeriodChange(value);
      return;
    }

    this.setState({
      modalActivePeriod: value,
    }, () => {
      this.generateTopicData(true);
    });
  }

  toggleChartExpandClick() {
    const { activePeriod, selectedTopic } = this.state;
    const {
      isFullLocations, onChartClick, activePeriod: propsActivePeriod, isInsideModal,
    } = this.props;

    if (onChartClick) {
      return onChartClick();
    }
    if (!isInsideModal) {
      this.setState({
        modalTabActive: 'trends',
      }, () => {
        if (!selectedTopic) {
          this.sentimentChartBoxRef.current.click();
        }
      });
    }
    this.setState({
      modalActivePeriod: isFullLocations ? activePeriod : propsActivePeriod,
    });
  }

  handleBoxMouseEnter() {
    const { isInsideModal, width, promoHeight } = this.props;

    clearTimeout(this.showTooltipTime);
    clearTimeout(this.showStoryModal);
    if (isInsideModal || width <= 1024) {
      return;
    }

    this.showTooltipTime = setTimeout(() => {
      const {
        top: offsetY, left: pageX,
      } = this.sentimentChartBoxRef.current.getBoundingClientRect();

      const pageY = window.pageYOffset + offsetY;
      const currentWidth = this.sentimentChartBoxRef.current.clientWidth / 2;

      const x = pageX + (currentWidth - (width >= 2800 ? 148 : 74));
      const y = pageY - promoHeight - (width >= 2800 ? 76 : 38);

      this.setState({
        boxChartTooltip: true,
        topPosition: y,
        leftPosition: x,
      });
    }, 500);
    this.showStoryModal = setTimeout(() => {
      const { selectedTopic } = this.state;
      if (selectedTopic) return;
      this.sentimentChartBoxRef.current.click();
    }, 3000);
  }

  handleBoxMouseLeave() {
    clearTimeout(this.showTooltipTime);
    clearTimeout(this.showStoryModal);
    this.setState({
      boxChartTooltip: false,
    });
  }

  handleChartMouseEnter() {
    const { tooltipHidden } = this.props;
    const { modalTabActive } = this.state;

    if (tooltipHidden || modalTabActive === 'trends') {
      this.setState({
        miniChartTooltip: false,
      });
      return;
    }
    this.setState({
      miniChartTooltip: true,
    });
  }

  handleChartMouseLeave() {
    this.setState({
      miniChartTooltip: false,
    });
  }

  async handleTopicRedirect() {
    const {
      history, topic: selectedTopic,
      isFullLocations, isTopicClass, isSectors,
    } = this.props;
    let topic;

    let topicURL = '';
    if (isFullLocations) {
      topicURL = '/directory/sentiment/locations';
    } else if (isTopicClass) {
      topicURL = `/news/directory/${selectedTopic.slug}`;
    } else if (isSectors) {
      topic = await SectorsSvc.getCategory(selectedTopic.entity_id);
      topicURL = `/news/directory/${SECTORS_PATH_ITEM}/${topic.subindustry.industry.sector.slug}/${topic.subindustry.industry.slug}/${topic.subindustry.slug}/${topic.slug}`;
    } else {
      topic = await TopicsSvc.getTopicById(selectedTopic.entity_id);
      const assetClassSlug = topic?.asset_class?.slug;
      const topicSlug = topic?.slug;
      topicURL = `/news/directory/${assetClassSlug}/${topicSlug}`;
    }

    history.push(topicURL);
  }

  handleTopicSelect(e) {
    const {
      width, promoHeight, isInsideModal, isChatbotOpened,
    } = this.props;

    clearTimeout(this.showStoryModal);
    if (
      isInsideModal
      || (e && this.tooltipRef?.current?.tooltipRef?.current?.contains(e.target))
      || (e && this.periodFiltersRef?.current?.contains(e.target))
    ) return;

    if (width <= 1024) {
      this.handleTopicRedirect();
      return;
    }

    const timeoutDelay = isChatbotOpened ? 800 : 0;

    setTimeout(() => {
      const { top: offsetY, left: pageX } = this.sentimentChartBoxRef.current.getBoundingClientRect();

      const pageY = window.pageYOffset + offsetY;
      const currentWidth = this.sentimentChartBoxRef.current.clientWidth / 2;
      const currentHeight = this.sentimentChartBoxRef.current.clientHeight;

      let x;
      let y = pageY + currentHeight + 15 - promoHeight;
      let arrowLeft = 'calc(50% - 9px)';
      if (width >= 2800) {
        y = pageY + currentHeight + 30 - promoHeight;
        if (pageX + 920 + currentWidth <= window.innerWidth) {
          if (pageX - 920 + currentWidth > 40) {
            x = pageX - 900 + currentWidth;
          } else {
            x = 40;
            arrowLeft = `${pageX + currentWidth - 58}px`;
          }
        } else {
          x = window.innerWidth - 1840;
          arrowLeft = `${pageX - x - 18 + currentWidth}px`;
        }
      } else if (pageX + 460 + currentWidth <= window.innerWidth) {
        if (pageX - 460 + currentWidth > 20) {
          x = pageX - 450 + currentWidth;
        } else {
          x = 20;
          arrowLeft = `${pageX + currentWidth - 29}px`;
        }
      } else {
        x = window.innerWidth - 920;
        arrowLeft = `${pageX - x - 9 + currentWidth}px`;
      }

      const {
        isTopic, slugType, topic,
      } = this.props;
      const query = isTopic ? getTrendingBarAssetQuery(topic, slugType) : '';

      this.setState({
        query,
        selectedTopic: {
          x,
          y,
          arrowLeft,
        },
      });
    }, timeoutDelay);
  }

  handleModalClose() {
    this.setState({
      selectedTopic: null,
      modalTabActive: '',
    });
  }

  handleModalTab(value) {
    this.setState({
      modalTabActive: value,
    });
  }

  render() {
    const {
      isMainBox, isSingleLine, title, activePeriod: propsActivePeriod,
      slugType, topic, isFullLocations, isTopic, isTopicClass, showTooltipOutside,
      isInsideModal, parentSentiment,
    } = this.props;
    const {
      isChartLoading, avgPoint, chartGraph, miniChartTooltip, query,
      chartLayout, isNoData, isChartHidden, selectedTopic, activePeriod,
      boxChartTooltip, topPosition, leftPosition, modalTabActive,
    } = this.state;

    const parentSentimentNumber = typeof (parentSentiment) === 'string' ? parentSentiment
      && Number(parentSentiment.slice(0, parentSentiment.length - 1)) : parentSentiment;

    const {
      text, icon, type, value,
    } = parentSentiment ? getAverageSentimentText(parentSentimentNumber) : avgPoint || {};
    const { id: topicId, slug: topicClassSlug } = topic || {};

    const currentPeriod = isFullLocations ? activePeriod : propsActivePeriod;
    const boxChartTooltipShow = boxChartTooltip && !miniChartTooltip && !selectedTopic;

    return (
      <>
        <div
          ref={this.sentimentChartBoxRef}
          className={cx('sentiment-chart', type, {
            main: isMainBox && !isSingleLine,
            single: isSingleLine,
            nodata: isNoData && !parentSentiment,
            modal: isInsideModal,
          })}
          onClick={this.handleTopicSelect}
          onMouseEnter={this.handleBoxMouseEnter}
          onMouseLeave={this.handleBoxMouseLeave}
        >
          <div className={cx('sentiment-chart-border', type)} />
          {!showTooltipOutside && (
            <div className={cx('sentiment-chart-tooltip-text', { active: boxChartTooltipShow })}>
              Click to see more info
            </div>
          )}
          {boxChartTooltipShow && showTooltipOutside && (
            <TooltipOutside
              topPosition={topPosition}
              leftPosition={leftPosition}
            />
          )}
          <div className={cx('sentiment-chart-header')}>
            <div className={cx('sentiment-chart-title')}>{title}</div>
            {isMainBox && (
              <>
                <Tooltip
                  className={cx('sentiment-chart-header-tooltip')}
                  hasBigScreenDesign
                  ref={this.tooltipRef}
                >
                  The average sentiment for all locations in our database,
                  indicating how positive or negative the global media currently is.
                </Tooltip>
                <PeriodFilters
                  onFilterChange={this.handlePeriodChange}
                  ref={this.periodFiltersRef}
                />
              </>
            )}
          </div>
          <div className={cx('sentiment-chart-body', type)}>
            {isChartLoading ? (
              <Preloader loading top="unset" className={cx('sentiment-chart-body-loading')} />
            ) : (
              <>
                {isNoData && !parentSentiment ? (
                  <div className={cx('sentiment-chart-body-no-data')}>
                    {isSingleLine ? (
                      <>
                        No data found for this time period. Try a longer period.
                      </>
                    ) : (
                      <>
                        No data for this period.
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className={cx('sentiment-value')}>
                      {parentSentiment ? (
                        <>
                          {Number(typeof (parentSentiment) === 'string' ? parentSentiment.slice(0, parentSentiment.length - 1) : parentSentiment) > 0 && '+'}
                          {typeof (parentSentiment) === 'string' ? parentSentiment : `${parentSentiment}%`}
                        </>
                      ) : (
                        <>
                          {value > 0 ? `+${value}` : value}
                          %
                        </>
                      )}
                    </div>
                    {!isChartHidden && (
                      <div
                        className={cx('sentiment-chart-mini')}
                        onClick={this.toggleChartExpandClick}
                        onMouseEnter={this.handleChartMouseEnter}
                        onMouseLeave={this.handleChartMouseLeave}
                      >
                        <div className={cx('sentiment-chart-mini-tooltip-text', { active: miniChartTooltip })}>
                          Сlick to expand Сhart
                        </div>
                        <div className={cx('sentiment-chart-mini-overlay')} />
                        <Plot
                          hideLoader
                          data={[chartGraph]}
                          layout={chartLayout}
                        />
                      </div>
                    )}
                    <div className={cx('sentiment-text')}>
                      {text}
                      <FontAwesomeIcon icon={icon} />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        {selectedTopic && (
          <SentimentModal
            activePeriod={currentPeriod}
            slugType={slugType}
            topicId={topicId}
            topicName={title}
            topicClassSlug={topicClassSlug}
            x={selectedTopic.x}
            y={selectedTopic.y}
            arrowLeft={selectedTopic.arrowLeft}
            sentimentContentRef={this.sentimentChartBoxRef}
            handleModalClose={this.handleModalClose}
            handleModalTab={this.handleModalTab}
            modalTabActive={modalTabActive}
            isFullLocations={isFullLocations}
            isMainBox={isMainBox}
            isTopic={isTopic}
            isTopicClass={isTopicClass}
            query={query}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = ({ common, promobar, chatbot }) => ({
  width: common.width,
  promoHeight: promobar.height || 0,
  isChatbotOpened: chatbot.isChatbotOpened,
});

export default connect(mapStateToProps, null)(withRouter(SentimentMiniChart));
